/**
 * Order Now — guided ordering page
 *
 * Flow: pick a delivery day → add ≥ minMeals meals → optional add-ons → checkout.
 * Rules (from theme settings → "Ordering rules", passed via data-attributes):
 *   - cut-off: orders for day X close `cutoffDaysBefore` days earlier at `cutoffHour`
 *   - a day with ≥ `minMeals` meals gets free delivery; a day with fewer meals is
 *     allowed only when a delivery-fee product is configured — it is then added
 *     automatically (one per day) and removed again once the day reaches minMeals
 *   - add-ons (bubble tea) unlock once a day has at least one meal
 *
 * Cart lines carry: `Delivery Date` (visible), `_day_date` (ISO), `_item_type`
 * ('meal' | 'addon' | 'fee'), `_cutoff_ts` (unix seconds, checked again by the cart page).
 *
 * Channels: the same class also drives the school-lunch page (sections/bbx-school-order.liquid,
 * `data-channel="school"`). School lines carry `_channel: 'school'` plus a visible `Deliver to`,
 * are delivered to one fixed location, close for the whole week at once (`data-cutoff-mode="weekly"`)
 * and never share a checkout with regular meals: each page shows the other channel's lines as
 * "not in this order" and blocks checkout until they are removed.
 */

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const pad = (n) => String(n).padStart(2, '0');
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
// cart.js returns titles with HTML entities already applied ("&amp;"); decode before re-escaping
const decodeEntities = (s) => { const t = document.createElement('textarea'); t.innerHTML = String(s ?? ''); return t.value; };
const DAY_RE = /^(mon|tue|wed|thu|fri|sat|sun)[a-z]*\.?\s*[\d.\/-]*$/i;
// "Yuzu Chicken Rice Box - Tuesday 9.8" → "Yuzu Chicken Rice Box" (the day is already the group heading)
const cleanTitle = (s) => decodeEntities(s)
  .replace(/\s*[-–—]\s*(mon|tue|wed|thu|fri|sat|sun)[a-z]*\.?\s*[\d.\/-]*$/i, '')
  .replace(/\((veg|vegetarian)\)/gi, '')
  .replace(/\u{1F336}\uFE0F?/gu, '')
  .replace(/\s{2,}/g, ' ').trim();

class OrderNowApp {
  constructor(appEl) {
    this.appEl = appEl;
    this.cart = { items: [], total_price: 0, attributes: {}, note: '' };
    this.days = [];
    this.dayByIso = {};
    this.activeIso = null;
    this.pendingSync = null;
    this.queue = []; // cart mutations waiting to be sent, in click order
    this.sending = false; // one request is on the wire
    this.reconcileRounds = 0;
    this.pendingExtras = null;
    this.variantCache = {};

    const d = appEl.dataset;
    this.cutoffHour = parseInt(d.cutoffHour || '12', 10);
    this.cutoffDaysBefore = parseInt(d.cutoffDaysBefore || '1', 10);
    this.minMeals = Math.max(1, parseInt(d.minMeals || '2', 10));
    // Store credit arrives formatted by Liquid's money_without_currency ("30.00", "1,250.00", "30,00")
    this.hasStoreCredit = d.storeCredit !== undefined;
    this.storeCreditCents = OrderNowApp.parseMoney(d.storeCredit);
    // % off the customer paid when buying their credit (e.g. 20 → paid 80% of face value)
    this.creditDiscountPct = Math.min(99, Math.max(0, parseFloat(d.creditDiscount || '0') || 0));
    // 'regular' (Order Now) or 'school' (school-lunch page). Lines without `_channel` are regular.
    this.channel = d.channel || 'regular';
    this.isSchool = this.channel === 'school';
    // 'day' = each day closes `cutoffDaysBefore` days before at `cutoffHour`;
    // 'weekly' = Mon–Sun weeks close together, `cutoffDaysBeforeMonday` days before the Monday at `cutoffHour`
    this.cutoffMode = d.cutoffMode === 'weekly' ? 'weekly' : 'day';
    // 'week': every open day is shown at once, grouped by week (school page); 'days': one day at a time with tabs
    this.weekLayout = d.layout === 'week';
    this.cutoffDaysBeforeMonday = Math.max(1, parseInt(d.cutoffDaysBeforeMonday || '1', 10));
    this.deliverTo = d.deliverTo || '';
    // Several schools configured: the parent picks one (searchable) and that becomes the order's "Deliver to"
    this.multiSchool = d.multiSchool === 'true';
    this.school = null; // { name, slug, label, time }
    this.otherChannelPage = d.otherChannelPage || (this.isSchool ? '/pages/order-now' : '');
    this.otherChannelLabel = d.otherChannelLabel || (this.isSchool ? 'Regular delivery order' : 'School lunch order');
    this.feeVariant = d.feeVariant ? Number(d.feeVariant) : null;
    this.feePrice = parseInt(d.feePrice || '0', 10);
    this.feeTitle = d.feeTitle || 'Delivery fee';
    if (this.feeVariant) this.variantCache[String(this.feeVariant)] = { title: this.feeTitle, image: null, price: this.feePrice };
    // Prepaid packs are paid for in their own checkout (see sections/package.liquid)
    this.packType = (d.packType || 'package').toLowerCase();
    this.packsPage = d.packsPage || '/pages/package';

    this.$ = (id) => document.getElementById(id);
    this.init();
  }

  async init() {
    await this.syncCart();
    await this.restoreParkedCart();
    this.buildVariantCache();
    this.setupDays();
    this.bindEvents();
    this.bindSchoolPicker();
    this.prefillExtras();
    this.refresh();
    this.offsetFloatingWidgets();
    setTimeout(() => this.offsetFloatingWidgets(), 2500);
    window.addEventListener('resize', () => this.offsetFloatingWidgets());
  }

  // ─── Days ───────────────────────────────────────────────────────────────────

  parseTitleDate(title) {
    const m = (title || '').match(/\((\d{1,2})\.(\d{1,2})\)/);
    if (!m) return null;
    const now = new Date();
    const date = new Date(now.getFullYear(), parseInt(m[1], 10) - 1, parseInt(m[2], 10));
    if (date < new Date(now.getFullYear(), now.getMonth() - 1, now.getDate())) date.setFullYear(now.getFullYear() + 1);
    // Same window as snippets/bbx-collection-date: yesterday … +horizon days, else not a delivery day
    const horizon = parseInt(this.appEl?.dataset.horizonDays || '45', 10);
    const min = new Date(now); min.setDate(min.getDate() - 1); min.setHours(0, 0, 0, 0);
    const max = new Date(now); max.setDate(max.getDate() + horizon + 1);
    if (date < min || date > max) return null;
    return date;
  }

  readDays() {
    const now = new Date();
    const days = [];
    document.querySelectorAll('[data-day-section]').forEach((el) => {
      const mf = (el.dataset.deliveryDate || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
      const date = mf
        ? new Date(parseInt(mf[1], 10), parseInt(mf[2], 10) - 1, parseInt(mf[3], 10))
        : this.parseTitleDate(el.dataset.collectionTitle);
      if (!date) return;
      date.setHours(0, 0, 0, 0);

      const cutoff = new Date(date);
      if (this.cutoffMode === 'weekly') {
        // Monday of the delivery week (Sunday belongs to the week before it)
        const dow = date.getDay();
        cutoff.setDate(cutoff.getDate() - ((dow + 6) % 7) - this.cutoffDaysBeforeMonday);
      } else {
        cutoff.setDate(cutoff.getDate() - this.cutoffDaysBefore);
      }
      cutoff.setHours(this.cutoffHour, 0, 0, 0);

      const isoDate = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
      const dayName = DAY_NAMES[date.getDay()];
      const info = {
        sectionId: el.dataset.daySection,
        el,
        isoDate,
        // ISO date of the Monday of this day's week: "order the whole week" works per week
        weekKey: (() => { const m = new Date(date); m.setDate(m.getDate() - ((date.getDay() + 6) % 7)); return `${m.getFullYear()}-${pad(m.getMonth() + 1)}-${pad(m.getDate())}`; })(),
        // Group order count published by the back office (school page), and the day's minimum
        groupOrders: parseInt(el.dataset.groupOrders || '0', 10) || 0,
        minOrders: parseInt(el.dataset.minOrders || '0', 10) || 0,
        // Multi-school: { slug: count } for this day, from the back office
        groupOrdersBySchool: (() => { try { return JSON.parse(el.dataset.groupOrdersBySchool || 'null') || {}; } catch (e) { return {}; } })(),
        hasCollection: el.dataset.hasCollection === 'true',
        isExpired: now >= cutoff,
        cutoffTs: Math.floor(cutoff.getTime() / 1000),
        cutoffLabel: cutoff.toLocaleString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }),
        shortName: dayName.charAt(0).toUpperCase() + dayName.slice(1, 3),
        dateLabel: date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }),
        fullLabel: date.toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' }),
      };
      days.push(info);
      this.dayByIso[isoDate] = info;
    });
    days.sort((a, b) => a.isoDate.localeCompare(b.isoDate));
    return days;
  }

  setupDays() {
    this.$('order-now-loading')?.setAttribute('hidden', '');
    this.days = this.readDays();

    const tabs = this.$('order-now-day-tabs');
    if (tabs) tabs.innerHTML = '';
    this.days.forEach((day) => {
      day.el.dataset.isoDate = day.isoDate;
      day.el.dataset.deliveryLabel = day.fullLabel;
      if (this.weekLayout || !tabs) return;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'on__daytab' + (day.isExpired ? ' is-closed' : day.hasCollection ? '' : ' is-soon');
      btn.dataset.navIso = day.isoDate;
      btn.disabled = day.isExpired;
      btn.setAttribute('aria-label', `${day.fullLabel}${day.isExpired ? ', ordering closed' : ''}`);
      btn.innerHTML = `
        <span class="on__daytab-wd">${day.shortName}</span>
        <span class="on__daytab-d">${day.dateLabel}</span>
        ${day.isExpired ? '<span class="on__daytab-tag">Closed</span>' : !day.hasCollection ? '<span class="on__daytab-tag">Soon</span>' : ''}
        <span class="on__daytab-badge" data-tab-count></span>`;
      if (!day.isExpired) btn.addEventListener('click', () => this.switchDay(day.isoDate, false));
      tabs.appendChild(btn);
      day.tab = btn;
    });

    const open = this.days.filter((d) => !d.isExpired);
    const noDays = this.$('order-now-no-days');
    if (noDays) noDays.hidden = open.length > 0;

    if (this.weekLayout) {
      // Every open day is visible at once; closed days and empty week groups are hidden
      this.days.forEach((d) => {
        d.el.hidden = d.isExpired;
        d.el.querySelector('[data-day-date]').textContent = d.fullLabel;
        d.el.querySelector('[data-day-cutoff]').textContent = `Order by ${d.cutoffLabel}`;
        d.el.querySelectorAll('.on__meal').forEach((card, i) => card.style.setProperty('--i', i));
        d.el.classList.add('is-showing');
      });
      document.querySelectorAll('[data-week-group]').forEach((g) => {
        const first = open.find((d) => d.weekKey === g.dataset.weekGroup);
        g.hidden = !first;
        const c = g.querySelector('[data-week-cutoff]');
        if (c && first) c.textContent = `Order by ${first.cutoffLabel}`;
      });
      this.activeIso = open[0]?.isoDate || null;
      this.updateDayUI();
      this.updateSteps();
      return;
    }

    // Start on the first open day that still needs meals, else the first open day
    const first = open.find((d) => d.hasCollection && this.getMealCount(d.isoDate) === 0) || open.find((d) => d.hasCollection) || open[0];
    if (first) this.switchDay(first.isoDate, false);
  }

  switchDay(isoDate, scroll = true) {
    const day = this.dayByIso[isoDate];
    if (!day || day.isExpired) return;
    this.activeIso = isoDate;
    if (this.weekLayout) { if (scroll) day.el.scrollIntoView({ block: 'start', behavior: 'smooth' }); return; }

    this.days.forEach((d) => {
      d.el.hidden = d.isoDate !== isoDate;
      d.tab?.classList.toggle('is-active', d.isoDate === isoDate);
      d.el.classList.remove('is-showing');
    });
    day.el.querySelectorAll('.on__meal').forEach((card, i) => card.style.setProperty('--i', i));
    void day.el.offsetWidth; // restart the entrance animation
    day.el.classList.add('is-showing');
    day.el.querySelector('[data-day-date]').textContent = day.fullLabel;
    day.el.querySelector('[data-day-cutoff]').textContent = `Order by ${day.cutoffLabel}`;
    // Keep the chosen tab visible inside the (horizontally scrolling) strip; never move the page on a tab click.
    day.tab?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    if (scroll) {
      // Only when asked to (e.g. "Add another day"): bring the day strip back into view
      const strip = this.$('order-now-day-tabs');
      const top = strip.getBoundingClientRect().top + window.scrollY - 80;
      if (top < window.scrollY) window.scrollTo({ top, behavior: 'smooth' });
    }
    this.updateDayUI();
    this.updateSteps();
  }

  nextDay() {
    const idx = this.days.findIndex((d) => d.isoDate === this.activeIso);
    const next = this.days.slice(idx + 1).find((d) => !d.isExpired && d.hasCollection) || this.days.find((d) => !d.isExpired && d.hasCollection && d.isoDate !== this.activeIso);
    if (next) this.switchDay(next.isoDate, true);
  }

  // ─── Cart API ───────────────────────────────────────────────────────────────

  async syncCart() {
    try {
      this.cart = await (await fetch('/cart.js')).json();
    } catch (e) {
      console.error('[OrderNow] cart sync failed', e);
    }
  }

  buildVariantCache() {
    document.querySelectorAll('[data-action="meal-increase"], [data-action="boba-sweetness"]').forEach((btn) => {
      const id = String(btn.dataset.variant || '');
      if (id && btn.dataset.title) {
        this.variantCache[id] = { title: btn.dataset.title, image: btn.dataset.image || null, price: parseInt(btn.dataset.price || '0', 10) };
      }
    });
  }

  lineProperties(isoDate, itemType) {
    const day = this.dayByIso[isoDate];
    const props = { 'Delivery Date': day?.fullLabel || isoDate, _day_date: isoDate, _item_type: itemType };
    if (day?.cutoffTs) props._cutoff_ts = String(day.cutoffTs);
    if (this.isSchool) props._channel = 'school';
    return props;
  }

  /** Line belongs to this page's channel (regular lines have no `_channel`). */
  isMine(item) { return (item.properties?._channel || 'regular') === this.channel; }
  /** A line placed by the other channel's page: it has a delivery day but is not ours. */
  isForeignLine(item) { return !!item.properties?._day_date && !this.isMine(item); }
  /** This channel's cart lines. Every day/meal lookup goes through here so the other channel is never counted. */
  lines() { return this.cart.items.filter((i) => this.isMine(i)); }

  findItem(variantId, isoDate) {
    return this.lines().find((i) => i.variant_id === Number(variantId) && i.properties?._day_date === isoDate) ?? null;
  }
  getQty(variantId, isoDate) { return this.findItem(variantId, isoDate)?.quantity ?? 0; }
  getMealCount(isoDate) {
    return this.lines().filter((i) => i.properties?._day_date === isoDate && (i.properties?._item_type || 'meal') === 'meal').reduce((s, i) => s + i.quantity, 0);
  }
  getFeeLine(isoDate) {
    return this.lines().find((i) => i.properties?._day_date === isoDate && i.properties?._item_type === 'fee') ?? null;
  }
  /** Day needs the fee: has meals but fewer than minMeals. */
  dayNeedsFee(isoDate) {
    const n = this.getMealCount(isoDate);
    return n > 0 && n < this.minMeals;
  }
  /** Day is orderable: enough meals for free delivery, or fee applied (when configured). */
  dayIsValid(isoDate) {
    const n = this.getMealCount(isoDate);
    if (n >= this.minMeals) return true;
    return n > 0 && !!this.feeVariant && !!this.getFeeLine(isoDate);
  }

  /**
   * Keep exactly one delivery-fee line on every day that needs it, none elsewhere.
   * Runs after every local cart change (optimistic) and after each server sync.
   */
  reconcileFees() {
    if (!this.feeVariant) return;
    const isoDates = [...new Set(this.lines().map((i) => i.properties?._day_date).filter(Boolean))];
    isoDates.forEach((iso) => {
      const fee = this.getFeeLine(iso);
      const needs = this.dayNeedsFee(iso);
      if (needs && !fee) {
        this.cart.items.push({
          variant_id: this.feeVariant, quantity: 1, price: this.feePrice, line_price: this.feePrice,
          title: this.feeTitle, image: null, variant_title: '', key: `optimistic-fee-${iso}`,
          properties: this.lineProperties(iso, 'fee'),
        });
        this.post('/cart/add.js', { id: this.feeVariant, quantity: 1, properties: this.lineProperties(iso, 'fee') }, 'fee add', `fee:${iso}`);
      } else if (fee && (!needs || fee.quantity !== 1)) {
        const keep = needs ? 1 : 0;
        if (keep) { fee.quantity = 1; fee.line_price = fee.price; }
        else this.cart.items = this.cart.items.filter((i) => i !== fee);
        if (String(fee.key).startsWith('optimistic-')) this.cancelQueued(`fee:${iso}`); // never sent: drop it
        else this.post('/cart/change.js', { id: fee.key, quantity: keep }, 'fee change');
      }
    });
  }
  isLineExpired(item) {
    const day = this.dayByIso[item.properties?._day_date];
    if (day) return day.isExpired;
    const ts = parseInt(item.properties?._cutoff_ts || '0', 10);
    return ts > 0 && Date.now() / 1000 >= ts;
  }

  cartAdd(variantId, isoDate, itemType) {
    this.reconcileRounds = 0;
    const existing = this.findItem(variantId, isoDate);
    if (existing) {
      existing.quantity += 1;
      existing.line_price = existing.price * existing.quantity;
    } else {
      const c = this.variantCache[String(variantId)] || {};
      this.cart.items.push({
        variant_id: Number(variantId), quantity: 1, price: c.price || 0, line_price: c.price || 0,
        title: c.title || '', image: c.image || null, variant_title: '',
        key: `optimistic-${variantId}-${isoDate}`, properties: this.lineProperties(isoDate, itemType),
      });
    }
    this.pop();
    this.refresh();
    this.post('/cart/add.js', { id: Number(variantId), quantity: 1, properties: this.lineProperties(isoDate, itemType) }, 'add');
  }

  cartChange(lineKey, qty) {
    this.reconcileRounds = 0;
    const item = this.cart.items.find((i) => i.key === lineKey);
    if (item) {
      if (qty <= 0) this.cart.items = this.cart.items.filter((i) => i.key !== lineKey);
      else { item.quantity = qty; item.line_price = item.price * qty; }
    }
    this.refresh();
    this.post('/cart/change.js', { id: lineKey, quantity: qty }, 'change');
  }

  async removeDay(isoDate) {
    const keys = this.lines().filter((i) => i.properties?._day_date === isoDate).map((i) => i.key);
    this.cart.items = this.cart.items.filter((i) => !keys.includes(i.key));
    this.refresh();
    for (const key of keys) {
      await this.post('/cart/change.js', { id: key, quantity: 0 }, 'remove');
    }
  }

  /** A line is a prepaid pack when its product type matches and it has no delivery day. */
  isPackLine(item) {
    return !item.properties?._day_date && (item.product_type || '').toLowerCase() === this.packType;
  }
  async removePacks() {
    const keys = this.cart.items.filter((i) => this.isPackLine(i)).map((i) => i.key);
    this.cart.items = this.cart.items.filter((i) => !keys.includes(i.key));
    this.refresh();
    for (const key of keys) await this.post('/cart/change.js', { id: key, quantity: 0 }, 'remove pack');
  }

  /** Take out every line the other channel's page put in the cart. */
  async removeForeign() {
    const keys = this.cart.items.filter((i) => this.isForeignLine(i)).map((i) => i.key);
    this.cart.items = this.cart.items.filter((i) => !keys.includes(i.key));
    this.refresh();
    for (const key of keys) await this.post('/cart/change.js', { id: key, quantity: 0 }, 'remove other channel');
  }

  /**
   * "Order the whole week": one portion of each day's first choice (the card marked
   * `data-week-default`, else the first orderable meal) on every open day of the
   * active day's week that has nothing yet. Days that already have a meal are left alone,
   * so the customer can pick a different dish first and then fill the rest.
   */
  orderWeek(weekKey) {
    if (!weekKey) weekKey = this.dayByIso[this.activeIso]?.weekKey;
    if (!weekKey) return 0;
    let added = 0;
    this.days.filter((d) => d.weekKey === weekKey && !d.isExpired && d.hasCollection && this.getMealCount(d.isoDate) === 0).forEach((d) => {
      const btn = d.el.querySelector('[data-week-default] [data-action="meal-increase"]:not(:disabled), .on__meal [data-action="meal-increase"]:not(:disabled)');
      if (!btn) return;
      this.cartAdd(btn.dataset.variant, d.isoDate, 'meal');
      added += 1;
    });
    return added;
  }

  /**
   * The packs page (without a Storefront API token) parks the meal cart in
   * localStorage before sending the pack through a cart permalink, which
   * replaces the cart. Put those meals back the next time this page loads.
   */
  async restoreParkedCart() {
    let parked = null;
    try { parked = JSON.parse(localStorage.getItem('bbx_parked_cart') || 'null'); } catch (e) { return; }
    if (!parked || !Array.isArray(parked.items)) return;
    try { localStorage.removeItem('bbx_parked_cart'); } catch (e) { /* ignore */ }
    if (Date.now() - (parked.ts || 0) > 48 * 3600 * 1000) return; // stale
    const have = new Set(this.cart.items.map((i) => OrderNowApp.lineId(i)));
    const missing = parked.items.filter((it) => it.id && it.quantity > 0 && !have.has(OrderNowApp.lineId({ variant_id: it.id, properties: it.properties || {} })));
    if (!missing.length) return;
    for (const it of missing) await this.post('/cart/add.js', { id: it.id, quantity: it.quantity, properties: it.properties || {} }, 'restore');
    if (parked.attributes && Object.keys(parked.attributes).length) await this.post('/cart/update.js', { attributes: parked.attributes, note: parked.note || '' }, 'restore extras');
    clearTimeout(this.pendingSync);
    await this.syncCart();
    this.restoredCount = missing.length;
  }

  async removeOrphans() {
    const isMealType = (i) => /^(dish|meal)$/i.test(i.product_type || '');
    const keys = this.cart.items.filter((i) => !i.properties?._day_date && !i.properties?.['Delivery Day'] && isMealType(i)).map((i) => i.key);
    this.cart.items = this.cart.items.filter((i) => !keys.includes(i.key));
    this.refresh();
    for (const key of keys) {
      await this.post('/cart/change.js', { id: key, quantity: 0 }, 'remove');
    }
  }

  /**
   * Cart mutations are queued and sent ONE AT A TIME, in click order.
   * Shopify does not promise to apply concurrent cart writes in order and
   * occasionally rejects one outright; sending them serially removes the
   * first problem and the reconcile pass in syncCartFromServer() repairs
   * the second. The local cart is the customer's intent until the server
   * has been read back and matches it.
   */
  post(path, payload, label, tag = null) {
    return new Promise((resolve) => {
      this.queue.push({ path, payload, label, tag, resolve });
      this.pump();
    });
  }
  /** Drop a queued request that has not been sent yet (e.g. a fee add that is no longer needed). */
  cancelQueued(tag) {
    this.queue = this.queue.filter((e) => { if (e.tag !== tag) return true; e.resolve(null); return false; });
  }
  get busy() { return this.sending || this.queue.length > 0; }
  pump() {
    if (this.sending) return;
    const e = this.queue.shift();
    if (!e) { this.debouncedSync(); return; }
    this.sending = true;
    fetch(e.path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(e.payload) })
      .then((r) => { if (r && r.ok === false) console.warn(`[OrderNow] ${e.label} rejected (${r.status}); will reconcile`); })
      .catch((err) => console.error(`[OrderNow] ${e.label} failed`, err))
      .finally(() => { this.sending = false; e.resolve(); this.pump(); });
  }

  debouncedSync() {
    clearTimeout(this.pendingSync);
    if (this.busy) return; // the queue drains into pump() → debouncedSync()
    this.pendingSync = setTimeout(() => this.syncCartFromServer(), 300);
  }

  /** Stable identity of a line regardless of its key: variant + properties. */
  static lineId(item) {
    const p = item.properties || {};
    return `${item.variant_id}|${JSON.stringify(Object.keys(p).sort().map((k) => [k, String(p[k])]))}`;
  }

  /**
   * Read the server cart. If it differs from what the customer asked for
   * (a rejected request, a write applied out of order), send the corrections
   * and read again — up to 3 rounds — before adopting the server state.
   */
  async syncCartFromServer() {
    if (this.busy) return;
    let server;
    try { server = await (await fetch('/cart.js')).json(); } catch (e) { console.error('[OrderNow] cart sync failed', e); return; }
    if (this.busy) return; // a new click happened meanwhile; its request will re-sync

    const intent = new Map();
    this.cart.items.forEach((i) => { const id = OrderNowApp.lineId(i); intent.set(id, (intent.get(id) || 0) + i.quantity); });
    const onServer = new Map();
    server.items.forEach((i) => { const id = OrderNowApp.lineId(i); onServer.set(id, i); });

    const fixes = [];
    onServer.forEach((line, id) => {
      const want = intent.get(id) || 0;
      if (line.quantity !== want) fixes.push(['/cart/change.js', { id: line.key, quantity: want }]);
    });
    this.cart.items.forEach((i) => {
      if (!onServer.has(OrderNowApp.lineId(i)) && i.quantity > 0) fixes.push(['/cart/add.js', { id: i.variant_id, quantity: i.quantity, properties: i.properties || {} }]);
    });

    if (fixes.length && this.reconcileRounds < 3) {
      this.reconcileRounds += 1;
      console.warn(`[OrderNow] server cart drifted; repairing ${fixes.length} line(s) (round ${this.reconcileRounds})`);
      fixes.forEach(([path, payload]) => this.post(path, payload, 'reconcile'));
      return; // the queue drain triggers the next sync
    }
    this.reconcileRounds = 0;

    server.items.forEach((it) => {
      const c = this.variantCache[String(it.variant_id)];
      if (c) { it.image ||= c.image; it.title ||= c.title; }
    });
    this.cart = server;
    this.refresh();
  }

  // ─── Delivery details (cart attributes) ────────────────────────────────────

  extrasPayload() {
    if (this.isSchool) {
      // School page: who the meal is for. The fixed location is stamped on the order too.
      const student = this.$('order-now-student')?.value.trim() || '';
      const grade = this.$('order-now-grade')?.value.trim() || '';
      const phone = this.$('order-now-phone')?.value.trim() || '';
      const attrs = { 'Deliver to': this.multiSchool ? (this.school?.label || '') : this.deliverTo, 'Student name': student, 'Grade / class': grade, 'Parent phone': phone };
      if (this.multiSchool) attrs['School'] = this.school?.name || '';
      return { note: '', attributes: attrs };
    }
    const note = this.$('order-now-note')?.value.trim() || '';
    const buzz = this.$('order-now-buzz')?.value.trim() || '';
    const utensils = this.$('order-now-utensils')?.checked ? 'Yes' : 'No';
    return { note, attributes: { 'Utensils': utensils, 'Buzz code': buzz, 'Delivery instructions': note } };
  }
  /** School page: the student's name is required so the box can be labelled. */
  studentMissing() { return this.isSchool && !!this.$('order-now-student') && !this.$('order-now-student').value.trim(); }
  /** School page with a school list: a school must be chosen before checkout. */
  schoolMissing() { return this.isSchool && this.multiSchool && !this.school; }

  // ─── School picker ─────────────────────────────────────────────────────────
  schoolOptions() { return [...document.querySelectorAll('[data-school-option]')].map((el) => ({ el, name: el.dataset.name, slug: el.dataset.slug, label: el.dataset.label, time: el.dataset.time || '', min: parseInt(el.dataset.min || '0', 10) || 0 })); }
  filterSchools(q) {
    const list = this.$('order-now-school-list'); if (!list) return;
    const terms = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    let shown = 0;
    this.schoolOptions().forEach((o) => {
      const hay = `${o.name} ${o.label}`.toLowerCase();
      const hit = terms.length === 0 || terms.every((t) => hay.includes(t));
      o.el.hidden = !hit; if (hit) shown += 1;
    });
    const none = list.querySelector('[data-school-none]'); if (none) none.hidden = shown > 0;
    list.hidden = false;
    this.$('order-now-school-input')?.setAttribute('aria-expanded', 'true');
  }
  closeSchoolList() { const list = this.$('order-now-school-list'); if (list) list.hidden = true; this.$('order-now-school-input')?.setAttribute('aria-expanded', 'false'); }
  selectSchool(slug, { save = true } = {}) {
    const o = this.schoolOptions().find((x) => x.slug === slug);
    this.school = o ? { name: o.name, slug: o.slug, label: o.label, time: o.time, min: o.min } : null;
    const chosen = this.$('order-now-school-chosen'), search = document.querySelector('[data-school-search]');
    if (chosen) chosen.hidden = !this.school;
    if (search) search.hidden = !!this.school;
    if (this.school) {
      const n = chosen.querySelector('[data-school-chosen-name]'), m = chosen.querySelector('[data-school-chosen-meta]');
      if (n) n.textContent = this.school.name;
      if (m) m.textContent = [this.school.label.replace(`${this.school.name} · `, '').replace(this.school.name, ''), this.school.time].filter(Boolean).join(' · ');
      const t = document.querySelector('[data-deliver-to-text]'); if (t) t.textContent = `${this.school.label}${this.school.time ? ` · ${this.school.time}` : ''}`;
      const l = document.querySelector('[data-location-school]'); if (l) l.textContent = `Your school: ${this.school.label}.`;
      try { localStorage.setItem('bbx_school', this.school.slug); } catch (e) { /* ignore */ }
    } else {
      const t = document.querySelector('[data-deliver-to-text]'); if (t) t.textContent = 'Delivered to your school';
      const l = document.querySelector('[data-location-school]'); if (l) l.textContent = 'Choose your school in the order panel.';
      const input = this.$('order-now-school-input'); if (input) { input.value = ''; input.focus(); }
    }
    this.closeSchoolList();
    if (save) this.scheduleSaveExtras();
    this.refresh();
  }
  bindSchoolPicker() {
    if (!this.multiSchool) return;
    const input = this.$('order-now-school-input'), list = this.$('order-now-school-list');
    if (!input || !list) return;
    input.addEventListener('input', () => this.filterSchools(input.value));
    input.addEventListener('focus', () => this.filterSchools(input.value));
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { this.closeSchoolList(); return; }
      if (e.key === 'Enter') { e.preventDefault(); const first = this.schoolOptions().find((o) => !o.el.hidden); if (first) this.selectSchool(first.slug); }
    });
    list.addEventListener('mousedown', (e) => { const opt = e.target.closest('[data-school-option]'); if (opt) { e.preventDefault(); this.selectSchool(opt.dataset.slug); } });
    document.addEventListener('click', (e) => { if (!e.target.closest('[data-school-picker]')) this.closeSchoolList(); });
    // Restore: the cart's School attribute first (same device or not), then the last choice on this device
    const bySchoolAttr = this.cart.attributes?.School && this.schoolOptions().find((o) => o.name === this.cart.attributes.School);
    let remembered = null; try { remembered = localStorage.getItem('bbx_school'); } catch (e) { /* ignore */ }
    const initial = bySchoolAttr?.slug || (remembered && this.schoolOptions().some((o) => o.slug === remembered) ? remembered : null);
    if (initial) this.selectSchool(initial, { save: false });
  }
  openRequest() { const dlg = this.$('order-now-request'); if (!dlg) return; this.closeSchoolList(); dlg.classList.add('is-open'); dlg.querySelector('input:not([type=hidden]), button')?.focus(); }
  closeRequest() { this.$('order-now-request')?.classList.remove('is-open'); }
  scheduleSaveExtras() {
    clearTimeout(this.pendingExtras);
    this.pendingExtras = setTimeout(() => this.saveExtras(), 600);
  }
  async saveExtras() {
    clearTimeout(this.pendingExtras);
    try {
      await this.post('/cart/update.js', this.extrasPayload(), 'extras save');
    } catch (e) {
      console.error('[OrderNow] attributes update failed', e);
    }
  }
  prefillExtras() {
    const a = this.cart.attributes || {};
    if (a['Student name'] && this.$('order-now-student')) this.$('order-now-student').value = a['Student name'];
    if (a['Grade / class'] && this.$('order-now-grade')) this.$('order-now-grade').value = a['Grade / class'];
    if (a['Parent phone'] && this.$('order-now-phone')) this.$('order-now-phone').value = a['Parent phone'];
    if (a['Buzz code'] && this.$('order-now-buzz')) this.$('order-now-buzz').value = a['Buzz code'];
    const note = a['Delivery instructions'] || this.cart.note || '';
    if (note && this.$('order-now-note')) this.$('order-now-note').value = note;
    if (this.$('order-now-utensils')) this.$('order-now-utensils').checked = a['Utensils'] === 'Yes';
  }

  // ─── Events ─────────────────────────────────────────────────────────────────

  bindEvents() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action]');
      if (!btn || btn.disabled) return;
      const action = btn.dataset.action;

      if (action === 'nutrition') {
        this.$('order-now-lightbox-img').src = btn.dataset.img;
        this.$('order-now-lightbox-img').alt = btn.dataset.alt || '';
        this.$('order-now-lightbox').classList.add('is-open');
        return;
      }
      if (action === 'meal-details') return this.openDetails(btn.closest('.on__meal'));
      if (action === 'school-request') return this.openRequest();
      if (action === 'school-change') return this.selectSchool(null);
      if (action === 'next-day') return this.nextDay();
      if (action === 'order-week') return this.orderWeek(btn.dataset.week || this.dayByIso[btn.closest('[data-day-section]')?.dataset.isoDate]?.weekKey);
      if (action === 'go-checkout') return window.innerWidth < 1024 ? this.openSheet() : this.$('order-now-checkout')?.focus();

      const dayEl = btn.closest('[data-day-section]');
      const isoDate = dayEl?.dataset.isoDate;
      if (!isoDate) return;

      if (action === 'meal-increase') this.cartAdd(btn.dataset.variant, isoDate, 'meal');
      else if (action === 'meal-decrease') {
        const item = this.findItem(btn.dataset.variant, isoDate);
        if (item) this.cartChange(item.key, item.quantity - 1);
      } else if (action === 'boba-sweetness') {
        const card = btn.closest('[data-boba-card]');
        const prev = card.dataset.selectedVariant;
        if (prev && prev !== btn.dataset.variant) {
          const prevItem = this.findItem(prev, isoDate);
          if (prevItem) this.cartChange(prevItem.key, 0);
        }
        card.querySelectorAll('[data-action="boba-sweetness"]').forEach((c) => c.setAttribute('aria-checked', c === btn ? 'true' : 'false'));
        card.dataset.selectedVariant = btn.dataset.variant;
        this.refresh();
      } else if (action === 'boba-increase') {
        const v = btn.closest('[data-boba-card]')?.dataset.selectedVariant;
        if (v) this.cartAdd(v, isoDate, 'addon');
      } else if (action === 'boba-decrease') {
        const v = btn.closest('[data-boba-card]')?.dataset.selectedVariant;
        const item = v && this.findItem(v, isoDate);
        if (item) this.cartChange(item.key, item.quantity - 1);
      }
    });

    // Summary line controls
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-cart-action]');
      if (!btn) return;
      const { cartAction, key, iso, variant, type } = btn.dataset;
      if (cartAction === 'remove-day') this.removeDay(iso);
      else if (cartAction === 'remove-orphans') this.removeOrphans();
      else if (cartAction === 'remove-packs') this.removePacks();
      else if (cartAction === 'remove-foreign') this.removeForeign();
      else if (cartAction === 'decrease') { const it = this.cart.items.find((i) => i.key === key); if (it) this.cartChange(key, it.quantity - 1); }
      else if (cartAction === 'increase') this.cartAdd(variant, iso, type || 'meal');
    });

    ['order-now-utensils', 'order-now-buzz', 'order-now-note', 'order-now-student', 'order-now-grade', 'order-now-phone'].forEach((id) => {
      this.$(id)?.addEventListener('input', () => { this.scheduleSaveExtras(); if (this.isSchool) this.renderSummary(); });
      this.$(id)?.addEventListener('change', () => this.scheduleSaveExtras());
    });

    this.$('order-now-checkout')?.addEventListener('click', () => this.proceedToCheckout());
    this.$('order-now-tab-cart')?.addEventListener('click', () => this.openSheet());
    this.$('order-now-bar-cta')?.addEventListener('click', (e) => {
      if (e.currentTarget.dataset.barCta === 'checkout') this.proceedToCheckout();
      else this.openSheet();
    });
    this.$('order-now-drawer-close')?.addEventListener('click', () => this.closeSheet());
    this.$('order-now-drawer-overlay')?.addEventListener('click', () => this.closeSheet());
    this.$('order-now-lightbox-close')?.addEventListener('click', () => this.$('order-now-lightbox').classList.remove('is-open'));
    this.$('order-now-lightbox')?.addEventListener('click', (e) => { if (e.target === e.currentTarget) e.currentTarget.classList.remove('is-open'); });
    this.$('order-now-detail-close')?.addEventListener('click', () => this.closeDetails());
    this.$('order-now-request-close')?.addEventListener('click', () => this.closeRequest());
    this.$('order-now-request')?.addEventListener('click', (e) => { if (e.target === e.currentTarget) this.closeRequest(); });
    // After a submitted request Shopify reloads the page with the form result: show it
    if (this.$('order-now-request') && (this.$('order-now-request').querySelector('[data-request-ok], .on__request-error') || /#school-request/.test(window.location.hash))) this.openRequest();
    this.$('order-now-detail')?.addEventListener('click', (e) => { if (e.target === e.currentTarget) this.closeDetails(); });
    this.$('order-now-detail-add')?.addEventListener('click', () => {
      // Same as tapping Add on the card: goes through cartAdd → post() like every other mutation
      const btn = this._detailCard?.querySelector('[data-action="meal-increase"]:not(:disabled)');
      this.closeDetails();
      if (btn) btn.click();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { this.closeSheet(); this.closeDetails(); this.closeRequest(); this.$('order-now-lightbox')?.classList.remove('is-open'); } });
  }

  // ─── UI ─────────────────────────────────────────────────────────────────────

  refresh() {
    this.reconcileFees();
    this.updateDayUI();
    this.renderSummary();
    this.updateSteps();
  }

  updateSteps() {
    if (this.weekLayout) {
      const any = this.days.some((d) => this.getMealCount(d.isoDate) > 0);
      const named = any && !this.studentMissing() && !this.schoolMissing();
      const steps = this.$('on-steps')?.querySelectorAll('[data-step]') || [];
      steps.forEach((s) => {
        const n = s.dataset.step;
        let state = '';
        if (n === '1') state = any ? 'is-done' : 'is-active';
        if (n === '2') state = named ? 'is-done' : any ? 'is-active' : '';
        if (n === '3') state = this._canCheckout ? 'is-active' : '';
        s.classList.remove('is-active', 'is-done');
        if (state) s.classList.add(state);
        s.querySelector('.on__step-n').textContent = state === 'is-done' ? '✓' : n;
      });
      return;
    }
    const activeCount = this.activeIso ? this.getMealCount(this.activeIso) : 0;
    const anyReady = this.days.some((d) => !d.isExpired && this.dayIsValid(d.isoDate));
    const steps = this.$('on-steps')?.querySelectorAll('[data-step]') || [];
    steps.forEach((s) => {
      const n = s.dataset.step;
      let state = '';
      if (n === '1') state = this.activeIso ? 'is-done' : 'is-active';
      if (n === '2') state = (this.activeIso && this.dayIsValid(this.activeIso)) || anyReady ? 'is-done' : this.activeIso ? 'is-active' : '';
      if (n === '3') state = this._canCheckout ? 'is-active' : '';
      s.classList.remove('is-active', 'is-done');
      if (state) s.classList.add(state);
      s.querySelector('.on__step-n').textContent = state === 'is-done' ? '✓' : n;
    });
  }

  updateDayUI() {
    this.days.forEach((day) => {
      const iso = day.isoDate;
      const mealCount = this.getMealCount(iso);
      const total = this.lines().filter((i) => i.properties?._day_date === iso && i.properties?._item_type !== 'fee').reduce((s, i) => s + i.quantity, 0);
      const ready = this.dayIsValid(iso);
      const free = mealCount >= this.minMeals;

      // Tab badge
      if (day.tab) {
        day.tab.classList.toggle('has-items', total > 0);
        day.tab.classList.toggle('is-ready', ready);
        const countEl = day.tab.querySelector('[data-tab-count]');
        if (countEl.textContent !== String(total || '')) { countEl.textContent = total || ''; if (total) { countEl.classList.remove('is-bump'); void countEl.offsetWidth; countEl.classList.add('is-bump'); } }
      }

      const el = day.el;
      const bar = el.querySelector('[data-daybar]');
      if (bar) {
        bar.classList.toggle('is-ready', ready);
        bar.classList.toggle('is-empty', mealCount === 0);
        const countEl = el.querySelector('[data-day-count]');
        if (countEl) countEl.textContent = mealCount;
        const prog = el.querySelector('[data-day-progress]');
        if (prog) prog.setAttribute('aria-valuenow', Math.min(mealCount, this.minMeals));
        const fill = el.querySelector('[data-day-progress-fill]');
        if (fill) fill.style.width = `${Math.min(100, (mealCount / this.minMeals) * 100)}%`;
        const msg = el.querySelector('[data-day-msg]');
        const more = this.minMeals - mealCount;
        const dayLabel = `${day.shortName} ${day.dateLabel}`;
        // Goal tag: what the customer unlocks, and how close they are
        bar.classList.toggle('is-close', mealCount > 0 && !free);
        bar.classList.toggle('is-free', free);
        const tagText = el.querySelector('[data-goal-tag-text]');
        if (tagText && !this.isSchool) {
          if (free) tagText.textContent = this.feeVariant ? 'Free delivery unlocked' : 'Day complete';
          else if (this.feeVariant) tagText.textContent = mealCount === 0 ? `Free delivery at ${this.minMeals} meals` : `${more} more → free delivery`;
          else tagText.textContent = mealCount === 0 ? `${this.minMeals} meals to go` : `${more} more to go`;
        }
        if (this.isSchool) {
          // Group progress: what the back office published for this day plus what this customer has added.
          // With several schools, only the chosen school's numbers count (and its own minimum, if set).
          const known = !this.multiSchool || !!this.school;
          const base = this.multiSchool ? (this.school ? (day.groupOrdersBySchool[this.school.slug] || 0) : 0) : day.groupOrders;
          const group = base + mealCount;
          const min = this.multiSchool && this.school?.min ? this.school.min : day.minOrders;
          const g = el.querySelector('[data-group-count]');
          if (g) g.textContent = group;
          const pick = el.querySelector('[data-group-line-pick]'), cnt = el.querySelector('[data-group-line-count]');
          if (pick) pick.hidden = known;
          if (cnt) {
            cnt.hidden = !known;
            const sch = cnt.querySelector('[data-group-school]'); if (sch) sch.textContent = this.school?.name || '';
            const mn = cnt.querySelector('[data-group-min]'); if (mn) mn.textContent = min;
            const pl = cnt.querySelector('[data-group-plural]'); if (pl) pl.textContent = group === 1 ? '' : 'es';
          }
          el.querySelectorAll('[data-dish-orders][data-by-school]').forEach((d) => {
            let by = {}; try { by = JSON.parse(d.dataset.bySchool || '{}'); } catch (e) { /* ignore */ }
            d.hidden = !known; if (known) d.textContent = `${by[this.school?.slug] || 0} ordered`;
          });
          if (!known) {
            const gp = el.querySelector('[data-group-progress-fill]'); if (gp) gp.style.width = '0%';
            bar.classList.remove('is-group-met');
            if (tagText) tagText.textContent = 'Choose your school';
          } else if (min > 0) {
            const gp = el.querySelector('[data-group-progress-fill]');
            if (gp) gp.style.width = `${Math.min(100, (group / min) * 100)}%`;
            const gpb = el.querySelector('[data-group-progress]');
            if (gpb) gpb.setAttribute('aria-valuenow', Math.min(group, min));
            bar.classList.toggle('is-group-met', group >= min);
            const left = min - group;
            if (tagText) tagText.textContent = group >= min ? 'Minimum reached' : `${left} more to confirm`;
          } else if (tagText) tagText.textContent = `${group} ordered`;
          if (!day.hasCollection) msg.textContent = 'Menu coming soon.';
          else if (mealCount === 0) msg.textContent = `Tap Add on one of the meals for ${dayLabel}.`;
          else msg.innerHTML = `✅ ${esc(dayLabel)}: <strong>${mealCount} meal${mealCount > 1 ? 's' : ''}</strong> for your student. Pick another day or check out.`;
        } else {
          if (!day.hasCollection) msg.textContent = 'Menu coming soon.';
          else if (mealCount === 0) msg.textContent = this.feeVariant
            ? `Add ${this.minMeals} meals for ${dayLabel} and delivery is on us.`
            : `Tap Add on ${this.minMeals} meals for ${dayLabel} to continue.`;
          else if (free) msg.innerHTML = this.feeVariant
            ? `🎉 <strong>Free delivery unlocked</strong> for ${esc(dayLabel)}. Add more, pick another day, or check out.`
            : `🎉 ${esc(dayLabel)} is ready. Add more, pick another day, or check out.`;
          else if (ready) msg.innerHTML = `Just <strong>${more} more meal${more > 1 ? 's' : ''}</strong> and delivery is free — you'd save <strong>${this.money(this.feePrice)}</strong>.`;
          else msg.textContent = `Add ${more} more meal${more > 1 ? 's' : ''} to complete this day.`;
        }
      }

      // Meal cards
      el.querySelectorAll('[data-meal-variant]').forEach((card) => {
        const qty = this.getQty(card.dataset.mealVariant, iso);
        card.classList.toggle('has-qty', qty > 0);
        card.querySelector('.on__add').hidden = qty > 0;
        card.querySelector('[data-stepper]').hidden = qty === 0;
        card.querySelector('[data-qty-display]').textContent = qty;
        const badge = card.querySelector('[data-meal-badge]');
        if (badge) {
          const prev = badge.dataset.qty;
          badge.hidden = qty === 0; badge.textContent = `×${qty}`; badge.setAttribute('aria-label', `${qty} in your order`);
          if (prev !== undefined && prev !== String(qty) && qty > 0) { badge.classList.remove('is-bump'); void badge.offsetWidth; badge.classList.add('is-bump'); }
          badge.dataset.qty = String(qty);
        }
      });

      // Add-ons
      const addons = el.querySelector('[data-addons]');
      if (addons) {
        addons.querySelector('[data-lock-msg]').hidden = ready;
        addons.querySelector('[data-addons-open]').hidden = !ready;
        addons.querySelectorAll('[data-boba-card]').forEach((card) => {
          const v = card.dataset.selectedVariant;
          const qty = v ? this.getQty(v, iso) : 0;
          const add = card.querySelector('[data-boba-add]');
          add.disabled = !v;
          add.textContent = v ? 'Add' : 'Pick sweetness';
          add.hidden = qty > 0;
          card.querySelector('[data-stepper]').hidden = qty === 0;
          card.querySelector('[data-boba-qty]').textContent = qty;
          card.classList.toggle('has-qty', qty > 0);
        });
      }

      // "What next" panel
      const next = el.querySelector('[data-day-next]');
      if (next) {
        next.hidden = !ready;
        next.querySelector('[data-day-next-label]').textContent = `${day.shortName} ${day.dateLabel}`;
      }
    });
  }

  renderSummary() {
    const lines = this.lines().filter((i) => i.properties?._day_date);
    // Meals added elsewhere (search, collection pages) without a delivery day can't be delivered
    const isMealType = (i) => /^(dish|meal)$/i.test(i.product_type || '');
    const orphans = this.cart.items.filter((i) => !i.properties?._day_date && !i.properties?.['Delivery Day'] && isMealType(i));
    const packs = this.cart.items.filter((i) => this.isPackLine(i));
    // Lines the other channel's page added: a different delivery, never the same checkout
    const foreign = this.cart.items.filter((i) => this.isForeignLine(i));
    const byDay = {};
    lines.forEach((it) => {
      const iso = it.properties._day_date;
      byDay[iso] ||= { label: this.dayByIso[iso]?.fullLabel || it.properties['Delivery Date'] || iso, items: [], mealCount: 0 };
      byDay[iso].items.push(it);
      if ((it.properties._item_type || 'meal') === 'meal') byDay[iso].mealCount += it.quantity;
    });
    // Show meals first, then add-ons, then the delivery fee line
    const order = { meal: 0, addon: 1, fee: 2 };
    Object.values(byDay).forEach((d) => d.items.sort((a, b) => (order[a.properties._item_type || 'meal'] ?? 0) - (order[b.properties._item_type || 'meal'] ?? 0)));

    const dates = Object.keys(byDay).sort();
    let canCheckout = dates.length > 0;
    let subtotal = 0, savings = 0, mealTotal = 0, itemTotal = 0;
    const blockers = [];
    let html = '';

    dates.forEach((iso) => {
      const d = byDay[iso];
      const expired = d.items.some((i) => this.isLineExpired(i));
      const missing = this.minMeals - d.mealCount;
      const hasFee = d.items.some((i) => i.properties._item_type === 'fee');
      const ok = !expired && this.dayIsValid(iso);
      if (!ok) canCheckout = false;
      if (expired) blockers.push(`Ordering has closed for ${d.label}. Remove it to continue.`);
      else if (!ok) blockers.push(`Add ${missing} more meal${missing > 1 ? 's' : ''} for ${d.label}.`);

      const tag = expired
        ? `<button type="button" class="on__cart-tag on__cart-tag--err" data-cart-action="remove-day" data-iso="${iso}">Closed · Remove</button>`
        : ok ? (hasFee ? '<span class="on__cart-tag on__cart-tag--warn">Delivery fee</span>' : `<span class="on__cart-tag on__cart-tag--ok">${this.isSchool ? 'School delivery' : 'Free delivery'}</span>`)
        : `<span class="on__cart-tag on__cart-tag--warn">${missing} more needed</span>`;
      html += `<div class="on__cart-day"><div class="on__cart-day-head"><span>${esc(d.label)}</span>${tag}</div>`;

      d.items.forEach((it) => {
        subtotal += it.line_price;
        if (it.properties._item_type === 'fee') {
          html += `<div class="on__line on__line--fee">
            <span class="on__line-img" aria-hidden="true">🚚</span>
            <div><span class="on__line-title">${esc(it.title)}</span><span class="on__line-sub">Add ${missing} more meal${missing > 1 ? 's' : ''} for free delivery</span></div>
            <span class="on__line-qty">${this.money(it.line_price)}</span>
          </div>`;
          return;
        }
        itemTotal += it.quantity;
        if ((it.properties._item_type || 'meal') === 'meal') mealTotal += it.quantity;
        if (it.compare_at_price && it.compare_at_price > it.price) savings += (it.compare_at_price - it.price) * it.quantity;
        const vt = decodeEntities(it.variant_title || '');
        const sub = vt && vt !== 'Default Title' && !DAY_RE.test(vt) ? `<span class="on__line-sub">${esc(vt)}</span>` : '';
        html += `<div class="on__line">
          ${it.image ? `<img class="on__line-img" src="${esc(it.image)}" alt="">` : '<span class="on__line-img"></span>'}
          <div><span class="on__line-title">${esc(cleanTitle(it.title))}</span>${sub}<span class="on__line-sub">${this.money(it.line_price)}</span></div>
          <div class="on__line-ctl">
            <button type="button" class="on__line-btn" data-cart-action="decrease" data-key="${esc(it.key)}" aria-label="Remove one">−</button>
            <span class="on__line-qty">${it.quantity}</span>
            <button type="button" class="on__line-btn" data-cart-action="increase" data-key="${esc(it.key)}" data-variant="${it.variant_id}" data-iso="${iso}" data-type="${esc(it.properties._item_type || 'meal')}" aria-label="Add one more">+</button>
          </div></div>`;
      });
      html += '</div>';
    });

    if (packs.length) {
      // A pack is paid for on its own (its order creates the store credit). It is not part of
      // this meal order: it stays visible here, is left out of the total, and is taken out of
      // the cart when the customer checks out.
      html += `<div class="on__cart-day on__cart-day--invalid"><div class="on__cart-day-head"><span>Prepaid pack · not in this order</span><button type="button" class="on__cart-tag on__cart-tag--err" data-cart-action="remove-packs">Remove</button></div>`;
      packs.forEach((it) => { html += `<div class="on__line"><span class="on__line-img"></span><div><span class="on__line-title">${esc(cleanTitle(it.title))}</span><span class="on__line-sub">${this.money(it.line_price)} · paid separately, removed at checkout · <a href="${esc(this.packsPage)}">Buy it on the packs page</a></span></div><span></span></div>`; });
      html += '</div>';
    }
    if (foreign.length) {
      canCheckout = false;
      const where = this.otherChannelPage ? `check them out on <a href="${esc(this.otherChannelPage)}">that page</a>` : 'check them out there';
      blockers.unshift(this.isSchool
        ? 'Your cart also has a regular delivery order. School lunches are delivered to the school, so remove those meals here or check them out on the Order Now page first.'
        : 'Your cart also has a school lunch order. It is delivered to the school, not to you, so remove it here or check it out on the school page first.');
      html += `<div class="on__cart-day on__cart-day--invalid"><div class="on__cart-day-head"><span>${esc(this.otherChannelLabel)} · not in this order</span><button type="button" class="on__cart-tag on__cart-tag--err" data-cart-action="remove-foreign">Remove</button></div>`;
      foreign.forEach((it) => { html += `<div class="on__line"><span class="on__line-img"></span><div><span class="on__line-title">${esc(cleanTitle(it.title))}</span><span class="on__line-sub">${esc(it.properties['Delivery Date'] || it.properties._day_date)} · ${this.money(it.line_price)} · ${where}</span></div><span></span></div>`; });
      html += '</div>';
    }
    if (orphans.length) {
      canCheckout = false;
      blockers.unshift('Some meals have no delivery day. Remove them and add them from the menu.');
      html += `<div class="on__cart-day on__cart-day--invalid"><div class="on__cart-day-head"><span>No delivery day</span><button type="button" class="on__cart-tag on__cart-tag--err" data-cart-action="remove-orphans">Remove</button></div>`;
      orphans.forEach((it) => { html += `<div class="on__line"><span class="on__line-img"></span><div><span class="on__line-title">${esc(cleanTitle(it.title))}</span><span class="on__line-sub">Added outside the menu · ${this.money(it.line_price)}</span></div><span></span></div>`; });
      html += '</div>';
    }
    if (this.schoolMissing() && dates.length > 0) {
      canCheckout = false;
      blockers.push('Choose your school so we know where to deliver.');
    }
    if (this.studentMissing() && dates.length > 0) {
      canCheckout = false;
      blockers.push("Enter your student's name so we can label the meal.");
    }
    const empty = `<p class="on__cart-empty">Nothing added yet. Pick a day and tap <strong>Add</strong> on a meal.</p>`;
    this.$('order-now-cart-items').innerHTML = html || empty;
    // Animate only lines that were not in the previous render (match by variant + day, so optimistic→server key swaps don't re-animate)
    const seen = this._seenLines || new Set();
    const nowSeen = new Set();
    this.$('order-now-cart-items').querySelectorAll('.on__line').forEach((el) => {
      const inc = el.querySelector('[data-cart-action="increase"]');
      const id = inc ? `${inc.dataset.variant}|${inc.dataset.iso}` : el.querySelector('.on__line-title')?.textContent || '';
      nowSeen.add(id);
      if (!seen.has(id) && seen.size + this._rendered > 0) el.classList.add('is-new');
    });
    this._seenLines = nowSeen; this._rendered = (this._rendered || 0) + 1;
    const totalEl = this.$('order-now-total');
    if (totalEl.textContent !== this.money(subtotal) && this._rendered > 1) { totalEl.classList.remove('is-tick'); void totalEl.offsetWidth; totalEl.classList.add('is-tick'); }
    totalEl.textContent = this.money(subtotal);

    // Store credit (Shopify customer balance): show what it covers and the remainder
    const creditRow = this.$('order-now-credit-row');
    const payRow = this.$('order-now-pay-row');
    if (creditRow && payRow) {
      const applied = Math.min(this.storeCreditCents, subtotal);
      // Logged-in customers always see their balance; the "You pay" line only matters once credit covers something
      const show = this.hasStoreCredit && subtotal > 0;
      creditRow.hidden = !show;
      payRow.hidden = !(show && applied > 0);
      if (show) {
        this.$('order-now-credit-balance').textContent = `(${this.money(this.storeCreditCents)} available)`;
        this.$('order-now-credit-applied').textContent = applied > 0 ? `−${this.money(applied)}` : this.money(0);
        this.$('order-now-pay').textContent = this.money(subtotal - applied);
      }
      // Savings from prepaid credit bought at a discount: the part of this order
      // covered by credit really cost the customer (100 − pct)% of its face value.
      const savedRow = this.$('order-now-saved-row');
      if (savedRow) {
        const saved = Math.round(applied * this.creditDiscountPct / 100);
        savedRow.hidden = !(show && saved > 0);
        if (saved > 0) {
          this.$('order-now-saved').textContent = this.money(saved);
          this.$('order-now-saved-note').textContent = `(${this.creditDiscountPct}% off with prepaid credit)`;
        }
      }
    }

    const savingsText = savings > 0 ? `You save ${this.money(savings)}` : '';
    const sv = this.$('order-now-savings');
    sv.textContent = savingsText;
    sv.hidden = !savingsText;

    // Top-up rows: how much this order would save with each credit discount
    let best = null;
    // Savings apply to the food value of the order (meals + add-ons), not to the delivery fee line
    const foodSubtotal = lines.filter((i) => i.properties._item_type !== 'fee').reduce((s, i) => s + i.line_price, 0);
    document.querySelectorAll('[data-topup]').forEach((row) => {
      const pct = parseFloat(row.dataset.topupPct || '0') || 0;
      const saving = Math.round(foodSubtotal * pct / 100);
      const has = foodSubtotal > 0;
      row.querySelector('[data-topup-full]').textContent = has ? this.money(foodSubtotal) : '—';
      row.querySelector('[data-topup-pay]').textContent = has ? this.money(foodSubtotal - saving) : '—';
      row.querySelector('[data-topup-saving]').textContent = has ? this.money(saving) : '—';
      row.classList.remove('is-best');
      if (saving > 0 && (!best || saving > best.saving)) best = { row, saving };
    });
    if (best) best.row.classList.add('is-best');

    this._canCheckout = canCheckout;
    this._hint = blockers[0] || (dates.length === 0
      ? (this.feeVariant || this.isSchool ? 'Add a meal for a delivery day to check out.' : `Add at least ${this.minMeals} meals for a delivery day to check out.`)
      : '');
    const btn = this.$('order-now-checkout');
    btn.disabled = !canCheckout;
    btn.textContent = canCheckout ? `Check out · ${this.money(subtotal)}` : 'Check out';
    this.$('order-now-checkout-hint').textContent = canCheckout ? '' : this._hint;

    // Mobile bar
    this.$('order-now-tab-badge').textContent = itemTotal;
    const barLabel = this.$('order-now-tab-cart-label');
    const barText = itemTotal ? `${this.money(subtotal)}` : 'Your order';
    if (barLabel.textContent !== barText && this._rendered > 1) { barLabel.classList.remove('is-tick'); void barLabel.offsetWidth; barLabel.classList.add('is-tick'); }
    barLabel.textContent = barText;
    this.$('order-now-bar-sub').textContent = itemTotal
      ? canCheckout ? `${mealTotal} meal${mealTotal !== 1 ? 's' : ''} · ${dates.length} day${dates.length !== 1 ? 's' : ''} · ready` : this._hint
      : 'No meals yet';
    const cta = this.$('order-now-bar-cta');
    cta.dataset.barCta = canCheckout ? 'checkout' : 'view';
    cta.textContent = canCheckout ? 'Check out' : 'View order';
    cta.classList.toggle('on__btn--ghost', !canCheckout);
  }

  /** Resolves once every queued cart request has been sent and answered. */
  async idle() {
    while (this.busy) await new Promise((r) => setTimeout(r, 50));
  }

  async proceedToCheckout() {
    this.renderSummary();
    if (!this._canCheckout) { this.openSheet(); return; }
    const btn = this.$('order-now-checkout');
    btn.disabled = true;
    btn.textContent = 'Taking you to checkout…';
    await this.saveExtras();
    // Packs are bought in their own checkout; make sure none rides along with the meals
    if (this.cart.items.some((i) => this.isPackLine(i))) {
      btn.textContent = 'Setting the pack aside…';
      await this.removePacks();
      await this.idle();
      await this.syncCart();
      if (this.cart.items.some((i) => this.isPackLine(i))) {
        this.refresh();
        btn.disabled = false;
        this.$('order-now-checkout-hint').textContent = 'The pack could not be removed from your cart. Please remove it above and try again.';
        return;
      }
    }
    await this.idle();
    if (window.__bbxNavigate) window.__bbxNavigate('/checkout'); else window.location.href = '/checkout';
  }

  /** Meal details popup: built from the card itself (photo, tags, nutrition) plus the hidden product description. */
  openDetails(card) {
    const dlg = this.$('order-now-detail');
    if (!card || !dlg) return;
    this._detailCard = card;
    const img = card.querySelector('.on__meal-media img');
    this.$('order-now-detail-media').innerHTML = img ? `<img src="${esc(img.currentSrc || img.src)}" alt="">` : '';
    this.$('order-now-detail-title').textContent = card.querySelector('.on__meal-title')?.textContent.trim() || '';
    const meta = [];
    const diet = card.querySelector('.on__diet'); if (diet) meta.push(`<ul class="on__diet on__diet--inline">${diet.innerHTML}</ul>`);
    const macros = card.querySelector('.on__macro-overlay, .on__macro-strip');
    if (macros) meta.push(`<dl class="on__macro-strip">${[...macros.querySelectorAll('.on__macro-item, .on__macro-strip > div')].map((m) => { const v = m.querySelector('strong, dd')?.textContent || ''; const l = m.querySelector('small, dt')?.textContent || ''; return `<div><dd>${esc(v)}</dd><dt>${esc(l)}</dt></div>`; }).join('')}</dl>`);
    const short = card.querySelector('.on__meal-desc'); if (short) meta.push(`<p class="on__detail-short">${esc(short.textContent)}</p>`);
    this.$('order-now-detail-meta').innerHTML = meta.join('');
    const src = card.querySelector('[data-meal-details]');
    this.$('order-now-detail-desc').innerHTML = src?.innerHTML.trim() || '';
    this.$('order-now-detail-price').textContent = src?.dataset.price || card.querySelector('.on__meal-price')?.textContent.trim() || '';
    const canAdd = !!card.querySelector('[data-action="meal-increase"]:not(:disabled)');
    const add = this.$('order-now-detail-add');
    add.hidden = !canAdd;
    dlg.classList.add('is-open');
    this.$('order-now-detail-close')?.focus();
  }
  closeDetails() { this.$('order-now-detail')?.classList.remove('is-open'); }

  openSheet() {
    this.$('order-now-summary').classList.add('is-open');
    this.$('order-now-drawer-overlay').classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }
  closeSheet() {
    this.$('order-now-summary').classList.remove('is-open');
    this.$('order-now-drawer-overlay').classList.remove('is-open');
    document.body.style.overflow = '';
  }
  pop() {
    const bar = this.$('order-now-tabbar');
    if (!bar) return;
    bar.classList.remove('is-pop'); void bar.offsetWidth; bar.classList.add('is-pop');
  }
  money(cents) { return '$' + (cents / 100).toFixed(2); }

  /** "1,250.00" | "1.250,00" | "30,00" | "30.00" | "30" → integer cents */
  static parseMoney(str) {
    if (str === undefined || str === null) return 0;
    let t = String(str).replace(/[^\d.,-]/g, '');
    if (!t) return 0;
    const lastDot = t.lastIndexOf('.'), lastComma = t.lastIndexOf(',');
    if (lastDot > -1 && lastComma > -1) t = lastDot > lastComma ? t.replace(/,/g, '') : t.replace(/\./g, '').replace(',', '.');
    else if (lastComma > -1) t = t.length - lastComma - 1 === 2 ? t.replace(',', '.') : t.replace(/,/g, '');
    const n = parseFloat(t);
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
  }

  /** Lift third-party chat bubbles above the mobile bottom bar. */
  offsetFloatingWidgets() {
    const bar = this.$('order-now-tabbar');
    if (!bar) return;
    const lift = window.innerWidth < 1024 && getComputedStyle(bar).display !== 'none' ? bar.offsetHeight : 0;
    document.querySelectorAll('body > *:not(script):not(style):not(link):not(dialog)').forEach((el) => {
      if ((el.id && el.id.startsWith('order-now')) || el.closest('#order-now-app')) return;
      const cs = getComputedStyle(el);
      if (el.dataset.orderNowLifted === undefined) {
        if (cs.position !== 'fixed' || cs.top !== 'auto') return;
        const bottom = parseFloat(cs.bottom);
        if (Number.isNaN(bottom) || bottom > 120 || el.offsetHeight > 200 || el.offsetWidth > window.innerWidth * 0.8) return;
        el.dataset.orderNowLifted = String(bottom);
      }
      el.style.bottom = `${(parseFloat(el.dataset.orderNowLifted) || 0) + lift}px`;
    });
  }
}

const boot = () => { const el = document.getElementById('order-now-app'); if (el) new OrderNowApp(el); };
document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', boot) : boot();
